import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Blend, ChevronDown, Download, Plus, Search } from 'lucide-react';
import type { Division, Position, Status } from '../../types/faculty';

interface FacultyFiltersProps {
  onFilterChange: (filters: FilterState) => void;
  onAddFaculty?: () => void;
  onExport?: () => void;
  filtersVisible?: boolean;
  onToggleFilters?: () => void;
}

export interface FilterState {
  search: string;
  division: Division | 'All divisions';
  status: Status | 'All statuses';
  position: Position | 'All positions';
  tags: string;
}

interface FilterOption {
  label: string;
  value: string;
}

interface FilterDropdownProps {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}

const divisionOptions: FilterOption[] = [
  { label: 'All divisions', value: 'All divisions' },
  { label: 'Boys', value: 'Boys' },
  { label: 'Girls', value: 'Girls' },
  { label: 'Preschool', value: 'Preschool' },
];

const statusOptions: FilterOption[] = [
  { label: 'All statuses', value: 'All statuses' },
  { label: 'Active', value: 'active' },
  { label: 'On Leave (Medical)', value: 'on_leave_medical' },
  { label: 'On Leave (Maternity)', value: 'on_leave_maternity' },
  { label: 'On Leave (Sabbatical)', value: 'on_leave_sabbatical' },
  { label: 'On Leave (Other)', value: 'on_leave_other' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Substitute', value: 'substitute' },
  { label: 'Temporary', value: 'temporary' },
  { label: 'Invited / New Hire', value: 'invited' },
  { label: 'Terminated', value: 'terminated' },
];

const positionOptions: FilterOption[] = [
  { label: 'All positions', value: 'All positions' },
  { label: 'Teacher', value: 'Teacher' },
  { label: "Teacher's Aide", value: "Teacher's Aide" },
  { label: 'Specialist', value: 'Specialist' },
  { label: 'Leadership', value: 'Leadership' },
  { label: 'Substitute', value: 'Substitute' },
  { label: 'Temporary', value: 'Temporary' },
  { label: 'Other', value: 'Other' },
];

const initialFilters = (): FilterState => ({
  search: '',
  division: 'All divisions',
  status: 'All statuses',
  position: 'All positions',
  tags: '',
});

function FilterDropdown({ label, value, options, onChange }: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const activeLabel = options.find((option) => option.value === value)?.label ?? value;
  const displayValue = activeLabel.startsWith('All ') ? 'All' : activeLabel;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex items-center gap-1.5 rounded-md px-1 py-1.5 transition-colors hover:bg-muted focus:outline-none"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="font-title text-[11px] font-medium uppercase tracking-wider leading-none text-text-tertiary">
          {label}
        </span>
        <span className="text-[13px] font-semibold leading-none text-iron-grey">{displayValue}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-text-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen ? (
        <div
          className="absolute left-0 top-full z-50 mt-1.5 max-h-64 min-w-[180px] overflow-y-auto rounded-lg border border-border bg-white py-1 shadow-md"
          role="listbox"
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                  isSelected
                    ? 'bg-muted font-medium text-text-secondary'
                    : 'text-iron-grey hover:bg-muted'
                }`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function FacultyFilters({
  onFilterChange,
  onAddFaculty,
  onExport,
  filtersVisible = false,
  onToggleFilters,
}: FacultyFiltersProps) {
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      onFilterChangeRef.current(filters);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [filters]);

  const handleChange = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3">
      <div className="relative min-w-0 flex-1">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
          <Search className="h-4 w-4 text-text-tertiary" />
        </div>
        <input
          data-testid="faculty-search"
          type="text"
          className="block w-full rounded-lg border border-border bg-white py-2 pl-10 pr-10 text-sm font-body text-iron-grey placeholder-text-tertiary leading-5 focus:outline-none focus:border-border"
          placeholder="Search faculty..."
          value={filters.search}
          onChange={(event) => handleChange('search', event.target.value)}
        />

        <button
          type="button"
          data-testid="faculty-filter-toggle"
          onClick={onToggleFilters}
          className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3.5 focus:outline-none"
          aria-label="Toggle filters"
          aria-pressed={filtersVisible}
        >
          <Blend
            className={`h-4 w-4 transition-colors ${
              filtersVisible ? 'text-text-secondary' : 'text-text-tertiary'
            }`}
          />
        </button>
      </div>

      {filtersVisible ? (
        <>
          <div className="h-6 w-px flex-shrink-0 bg-border" />
          <div className="flex flex-shrink-0 items-center gap-2">
            <FilterDropdown
              label="Division"
              value={filters.division}
              options={divisionOptions}
              onChange={(value) => handleChange('division', value as FilterState['division'])}
            />
            <FilterDropdown
              label="Status"
              value={filters.status}
              options={statusOptions}
              onChange={(value) => handleChange('status', value as FilterState['status'])}
            />
            <FilterDropdown
              label="Position"
              value={filters.position}
              options={positionOptions}
              onChange={(value) => handleChange('position', value as FilterState['position'])}
            />
          </div>
        </>
      ) : null}

      <div className="h-6 w-px flex-shrink-0 bg-border" />

      {onExport ? (
        <button
          type="button"
          data-testid="export-btn"
          onClick={onExport}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-sm font-medium text-iron-grey transition-colors hover:bg-muted focus:outline-none"
        >
          <Download className="h-4 w-4" />
          Export
        </button>
      ) : null}

      {onAddFaculty ? (
        <button
          type="button"
          data-testid="add-faculty-btn"
          onClick={onAddFaculty}
          className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-primary text-white transition-colors hover:bg-primary-hover focus:outline-none"
          aria-label="Add faculty"
        >
          <Plus className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}
